import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router";
import * as Analytics from "../../util/Analytics";
import EventBus from "../../util/EventBus";
import {useHandler} from "../../util/useHandler";
import CCButton from "../Common/CCButton";
import useAppContext from "../../util/AppContext";

// What the emitter knew, when it knew anything. Shaped by systemErrorDetail in EnterpriseComponent.
type SystemErrorDetail = {code?: string; status?: number; url?: string; message?: string;
                         component?: string; operation?: string};

export function SystemError() {

    const [visible, setVisible] = useState(false);
    // WHAT WENT WRONG, WHEN THE EMITTER KNOWS. Every caller of the enterprise component's four
    // *Failed handlers was already passing the error in; the handlers dropped it, so this dialog had
    // nothing to show and neither did the person reading it. It now arrives here.
    const [detail, setDetail] = useState<SystemErrorDetail | null>(null);

    const navigate = useNavigate();
    const appContext = useAppContext();

    const updateListener = useHandler((incoming?: SystemErrorDetail) => {
        // RECORDED ON THE ANALYTICS EVENT TOO. "System Error Viewed" with no dimensions could say how
        // OFTEN this dialog appears and never which failure caused it, which is the one thing that
        // would have made the count actionable.
        Analytics.record("System Error Viewed", {
            Code: incoming?.code ?? null, Status: incoming?.status ?? null, Url: incoming?.url ?? null,
            Component: incoming?.component ?? null, Operation: incoming?.operation ?? null,
        });
        setDetail(incoming ?? null);
        setVisible(true);
    }, []);

    useEffect(() => {
        EventBus.error.addListener("show-system-error", updateListener);
        return () => {
            EventBus.session.removeListener("update", updateListener);
        }
    }, [updateListener]);

    return visible ? (
        <Dialog open={true} scroll="body">
            <DialogTitle>System Error</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    An unexpected system error has occurred. To continue, please reload the page or logout.
                </DialogContentText>
                {/* SHOWN ONLY WHEN THERE IS SOMETHING TO SHOW, so a failure that genuinely carries no
                    detail reads exactly as it did before rather than gaining an empty line. The code
                    is the quotable part: it survives prod stripping the server's own sentence, and it
                    is what turns "it threw a system error" into something somebody can look up. */}
                {/* ANYTHING WE KNOW, rather than only an HTTP code. A failure with no response --
                    a thrown TypeError, a dropped connection -- has a message and no code, and the
                    first version of this hid the line entirely in exactly that case, which is the
                    blank dialog it was written to stop. Shown as one line somebody can quote. */}
                {detail && (detail.code || detail.status || detail.message || detail.component) ? (
                    <DialogContentText sx={{mt: 1.5, fontSize: 13, fontFamily: "monospace",
                                            wordBreak: "break-word"}}>
                        {[
                            detail.component && detail.operation
                                ? `${detail.operation} ${detail.component}` : detail.component,
                            detail.code,
                            detail.status ? `HTTP ${detail.status}` : null,
                            detail.message,
                            detail.url,
                        ].filter(Boolean).join(" · ")}
                    </DialogContentText>
                ) : null}
            </DialogContent>
            <DialogActions>
                {appContext?.sessionContext.valid() ? (
                    <CCButton
                        color="secondary"
                        onClick={async () => {
                            Analytics.record("System Error Closing", {
                                Reason: "Logout",
                            });
                            setVisible(false);
                            navigate("/logout");
                        }}
                    >
                        Logout
                    </CCButton>
                ) : null}
                <CCButton
                    onClick={async () => {
                        Analytics.record("System Error Closing", {
                            Reason: "Reload",
                        });
                        setVisible(false);
                        window.location.reload();
                    }}
                >
                    Reload
                </CCButton>
            </DialogActions>
        </Dialog>
    ) : null;
}
